import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "../../components/Layout/Layout";
import client from "../../api/client";
import User from "../../components/User";
import CdekWaybillModal from "../../components/CdekWaybillModal";
import { useInHouse } from "../../context/inHousePermissions";
import {
  effectiveDayKey,
  dayKeyToLabel,
  weekdayAbbr,
  todayKeyUTC5,
  yesterdayKeyUTC5,
  addDayKey,
  shiftWeekendToMonday,
} from "../../utils/storeDay";
// Переиспользуем стили таблицы/кнопок со страницы продаж (.tbl, .btn).
import "../sales/index.css";
import "./log.css";

// Строка с бэка GET /api/admin/archives/logistics.
interface LogiRow {
  userId: number;
  username: string | null;
  status: string;
  address: string | null;
  city: string | null;
  country: string | null;
  recipient: string | null;
  whatsapp: string | null;
  telegram: string | null;
  logist: string | null;
  logMethod: string | null;
  cdekNumber: string | null;
  comment: string | null;
  isInfluencer: boolean;
  express: "express_print" | "express_edit" | "urep" | "none" | null;
  storeDate: string | null;
  createdAt: string;
  bookName: string | null;
  author: string | null;
}

// Кнопка-действие в зависимости от текущего статуса: подпись + следующий статус.
const NEXT_ACTION: Record<string, { label: string; next: string }> = {
  "Отправлено на печать": { label: "Принять", next: "Отпечатано" },
  Отпечатано: { label: "Отправить", next: "Отправлено" },
  Отправлено: { label: "Доставлено", next: "Доставлено" },
};

// Экспресс-книги (urep + express_print) попадают в логистику в тот же день,
// что и в хранилище. Обычные (включая express_edit и none) — на следующий
// день. Выходные не используются: если день попал на сб/вс — переносим на
// понедельник (как в хранилище).
const isLogiExpress = (e: LogiRow["express"]) =>
  e === "urep" || e === "express_print";

const logiDayKey = (r: LogiRow): string => {
  const k = effectiveDayKey(r);
  // Базовый ключ (effectiveDayKey) уже сдвинут с выходных в storeDayKey, но
  // ручной storeDate-override или +1 для обычных могут попасть на выходной —
  // поэтому всегда нормализуем результат на будний день.
  return shiftWeekendToMonday(isLogiExpress(r.express) ? k : addDayKey(k, 1));
};

// Чипы-фильтры сверху страницы — по «что надо сделать» (по статусу заказа).
type StatusFilter = "" | "accept" | "send" | "deliver";
const STATUS_FILTERS: { value: StatusFilter; label: string; status?: string }[] = [
  { value: "", label: "Все" },
  { value: "accept", label: "Надо принять", status: "Отправлено на печать" },
  { value: "send", label: "Надо отправить", status: "Отпечатано" },
  { value: "deliver", label: "Надо доставить", status: "Отправлено" },
];

// Разбивка дня по статусам в заголовке (что и сколько лежит на каждом этапе).
// Нулевые не показываем.
const STATUS_BREAKDOWN: { label: string; status: string }[] = [
  { label: "На печати", status: "Отправлено на печать" },
  { label: "Ждет отправки", status: "Отпечатано" },
  { label: "Ждет доставки", status: "Отправлено" },
];

// Массовые действия в каждой таблице: какие статусы переводятся куда.
type BulkAction = "accept" | "send" | "deliver";
const BULK_ACTIONS: Record<BulkAction, { label: string; from: string; to: string }> = {
  accept:  { label: "Принять",   from: "Отправлено на печать", to: "Отпечатано" },
  send:    { label: "Отправить", from: "Отпечатано",            to: "Отправлено" },
  deliver: { label: "Доставить", from: "Отправлено",            to: "Доставлено" },
};
const BULK_ORDER: BulkAction[] = ["accept", "send", "deliver"];

// Способы доставки — тот же список, что в карточке заказа (User.tsx).
const LOG_METHOD_OPTIONS = ["sdek", "indriver", "dhl", "курьер", "kazpost", "авиа"];

// Заказ нельзя отправить, пока не известно чем. А для СДЭК нужен ещё и номер
// накладной: без него не узнать плановую дату и не закрыть заказ автоматом,
// поэтому бэкенд такой PATCH отклоняет (см. routes/orders.ts). Собираем всё
// недостающее одним экраном, чтобы логист не ходил по карточкам поштучно.
const needsDelivery = (
  r: LogiRow,
  method: string | undefined,
  cdek: string | undefined
): boolean => {
  const m = (method ?? r.logMethod ?? "").trim();
  if (!m) return true;
  if (m !== "sdek") return false;
  return !(cdek ?? r.cdekNumber ?? "").trim();
};

// wa.me-ссылка из номера.
const waLink = (num: string): string =>
  `https://wa.me/${num.replace(/\D/g, "")}`;

const Logistics: React.FC = () => {
  const [rows, setRows] = useState<LogiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const { currentUserRecord } = useInHouse();
  const todayKey = todayKeyUTC5();
  const yesterdayKey = yesterdayKeyUTC5();
  // По умолчанию раскрыт только «вчерашний» день; остальные показаны как
  // заголовок-плашка, по клику раскрываются.
  const [openDays, setOpenDays] = useState<Set<string>>(
    () => new Set([yesterdayKey])
  );
  const toggleDay = (key: string) =>
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Попап массовых действий (как в /storage «Отправить на печать»).
  const [bulkPopup, setBulkPopup] = useState<{ action: BulkAction; dayKey: string } | null>(null);
  const [bulkChecked, setBulkChecked] = useState<Record<number, boolean>>({});
  const [bulkSaving, setBulkSaving] = useState(false);
  // Второй шаг попапа «Отправить»: добор способа доставки и номера СДЭК.
  const [bulkStep, setBulkStep] = useState<"list" | "delivery">("list");
  const [bulkMethod, setBulkMethod] = useState<Record<number, string>>({});
  const [bulkCdek, setBulkCdek] = useState<Record<number, string>>({});
  // Ошибка смены статуса. Показываем попапом, а не alert'ом: бэкенд
  // отвечает осмысленным текстом (например, «Для доставки СДЭК укажите
  // номер накладной»), и его надо показать логисту, а не прятать в консоль.
  const [errorPopup, setErrorPopup] = useState<string | null>(null);
  // Попап накладной СДЭК при «Отправить» у одной книги.
  // inBulk — открыт со сверки массовой отправки: после создания только
  // проставляем номер, статус меняет общая кнопка.
  const [waybillRow, setWaybillRow] = useState<{ row: LogiRow; inBulk: boolean } | null>(null);

  // Попап User по клику на строку.
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);

  const handleRowClick = async (r: LogiRow) => {
    if (!r.username || rowLoadingId) return;
    setRowLoadingId(r.userId);
    try {
      const { data } = await client.get(`/orders/${encodeURIComponent(r.username)}`);
      setSelectedUser({ username: r.username, ...data });
    } catch (err) {
      console.error("[log] failed to load order", err);
    } finally {
      setRowLoadingId(null);
    }
  };

  useEffect(() => {
    client
      .get<LogiRow[]>("/admin/archives/logistics")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((err) => console.error("[log] fetch failed", err))
      .finally(() => setLoading(false));
  }, []);

  // Применяем фильтр по статусу до группировки.
  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    const target = STATUS_FILTERS.find((f) => f.value === statusFilter)?.status;
    if (!target) return rows;
    return rows.filter((r) => r.status === target);
  }, [rows, statusFilter]);

  // Группируем по «логистическому» дню: для обычной печати +1 от складского
  // дня, для экспресса — тот же день, что и в хранилище.
  const days = useMemo(() => {
    const map = new Map<string, LogiRow[]>();
    for (const r of filteredRows) {
      const k = logiDayKey(r);
      const arr = map.get(k);
      if (arr) arr.push(r);
      else map.set(k, [r]);
    }
    // Ключи YYYY-MM-DD сортируются лексикографически = хронологически, DESC.
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, items]) => ({ key, items }));
  }, [filteredRows]);

  const advance = async (row: LogiRow, skipWaybill = false) => {
    const action = NEXT_ACTION[row.status];
    if (!action || !row.username) return;

    // Отправка без накладной — сперва предлагаем создать накладную СДЭК
    // через API. «Пропустить» ведёт дальше по старому пути: способ и номер
    // руками.
    if (action.next === "Отправлено" && !skipWaybill && !(row.cdekNumber ?? "").trim()) {
      setWaybillRow({ row, inBulk: false });
      return;
    }

    // Отправка без способа доставки (или СДЭКом без накладной) — не ошибка,
    // а недостающие данные. Вместо отказа открываем тот же экран сверки,
    // что и при массовой отправке, но с одной этой книгой: логист заполняет
    // недостающее и отправляет отсюда же.
    if (action.next === "Отправлено" && needsDelivery(row, undefined, undefined)) {
      setBulkChecked({ [row.userId]: true });
      setBulkMethod({});
      setBulkCdek({});
      setBulkStep("delivery");
      setBulkPopup({ action: "send", dayKey: logiDayKey(row) });
      return;
    }

    setBusyUserId(row.userId);
    try {
      // Логиста ставим, только если он ещё не назначен — не перетираем
      // того, кто уже ведёт книгу.
      const me = currentUserRecord?.name || "";
      const shouldSetLogist = !!me && !row.logist;
      const patch: Record<string, string> = { status: action.next };
      if (shouldSetLogist) patch.logist = me;
      await client.patch(`/orders/${encodeURIComponent(row.username)}`, patch);
      setRows((prev) =>
        prev.map((r) =>
          r.userId === row.userId
            ? { ...r, status: action.next, logist: shouldSetLogist ? me : r.logist }
            : r
        )
      );
    } catch (err: any) {
      console.error("[log] status change failed", err);
      setErrorPopup(err?.response?.data?.error || "Не удалось сменить статус");
    } finally {
      setBusyUserId(null);
    }
  };

  // Открыть попап массового действия для дня. По дефолту все юзеры с
   // подходящим статусом отмечены чекбоксами.
  const openBulk = (dayKey: string, action: BulkAction) => {
    const items = (days.find((d) => d.key === dayKey)?.items ?? []).filter(
      (r) => r.status === BULK_ACTIONS[action].from && r.username
    );
    const checked: Record<number, boolean> = {};
    items.forEach((r) => {
      checked[r.userId] = true;
    });
    setBulkChecked(checked);
    setBulkStep("list");
    setBulkMethod({});
    setBulkCdek({});
    setBulkPopup({ action, dayKey });
  };

  const handleBulkSubmit = async () => {
    if (!bulkPopup) return;
    const cfg = BULK_ACTIONS[bulkPopup.action];
    const day = days.find((d) => d.key === bulkPopup.dayKey);
    if (!day) {
      setBulkPopup(null);
      return;
    }
    const targets = day.items.filter(
      (r) => r.status === cfg.from && r.username && bulkChecked[r.userId]
    );
    if (targets.length === 0) {
      setBulkPopup(null);
      return;
    }

    // Отправка всегда проходит через второй шаг — сверку способов доставки
    // и номеров, даже когда всё уже заполнено. Это последний экран перед
    // тем, как книги уедут, и на нём видно, чем именно едет каждая.
    if (bulkPopup.action === "send" && bulkStep === "list") {
      setBulkStep("delivery");
      return;
    }

    setBulkSaving(true);
    try {
      const me = currentUserRecord?.name || "";
      await Promise.all(
        targets.map((t) => {
          const patch: Record<string, string> = { status: cfg.to };
          if (me && !t.logist) patch.logist = me;
          // Уже проставленное не переписываем — только доливаем недостающее.
          const m = (bulkMethod[t.userId] ?? "").trim();
          if (m && !t.logMethod) patch.logMethod = m;
          const num = (bulkCdek[t.userId] ?? "").trim();
          if (num && !t.cdekNumber) patch.cdekNumber = num;
          return client.patch(`/orders/${encodeURIComponent(t.username!)}`, patch);
        })
      );
      const ids = new Set(targets.map((t) => t.userId));
      setRows((prev) =>
        prev.map((r) =>
          ids.has(r.userId)
            ? {
                ...r,
                status: cfg.to,
                logist: !!me && !r.logist ? me : r.logist,
                logMethod: r.logMethod || (bulkMethod[r.userId] ?? "").trim() || null,
                cdekNumber: r.cdekNumber || (bulkCdek[r.userId] ?? "").trim() || null,
              }
            : r
        )
      );
      setBulkPopup(null);
    } catch (err: any) {
      console.error("[log] bulk action failed", err);
      setErrorPopup(
        err?.response?.data?.error || "Не удалось массово сменить статусы"
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const cityCountry = (r: LogiRow) =>
    [r.city, r.country].filter(Boolean).join(", ") || "—";

  return (
    <Layout>
      <div className="sales-head">
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>
          Логистика
        </h2>
      </div>

      <div className="filters sales-mgr-filter">
        {STATUS_FILTERS.map((f) => {
          const cnt =
            f.value === "" ? rows.length : rows.filter((r) => r.status === f.status).length;
          return (
            <button
              key={f.value || "all"}
              type="button"
              className={`sales-mgr-btn${statusFilter === f.value ? " sales-mgr-btn--active" : ""}`}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
              {f.value !== "" && cnt > 0 && (
                <span style={{ marginLeft: 6, opacity: 0.7 }}>({cnt})</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ color: "var(--text-3)" }}>Загрузка…</div>
      ) : days.length === 0 ? (
        <div style={{ color: "var(--text-3)" }}>Пока нечего отправлять</div>
      ) : (
        days.map(({ key, items }) => {
          const isOpen = openDays.has(key);
          const dateLabel =
            key === todayKey
              ? "Сегодня"
              : key === yesterdayKey
                ? "Вчера"
                : dayKeyToLabel(key);
          return (
          <section key={key} className="log-day">
            <button
              type="button"
              className={`log-day-header${isOpen ? " log-day-header--open" : ""}`}
              onClick={() => toggleDay(key)}
              aria-expanded={isOpen}
            >
              <span className="log-day-title">
                {dateLabel}, {weekdayAbbr(key)}
                <span className="log-day-count"> · {items.length}</span>
                {STATUS_BREAKDOWN.map(({ label, status }) => {
                  const cnt = items.filter((r) => r.status === status).length;
                  return cnt > 0 ? (
                    <span key={status} className="log-day-stat">
                      , {label} ({cnt})
                    </span>
                  ) : null;
                })}
              </span>
              <span className="log-day-chevron">{isOpen ? "▴" : "▾"}</span>
            </button>

            {isOpen && (
            <>
            <div className="log-day-actions">
              {BULK_ORDER.map((a) => {
                const cnt = items.filter((r) => r.status === BULK_ACTIONS[a].from).length;
                return (
                  <button
                    key={a}
                    type="button"
                    className="log-bulk-btn"
                    disabled={cnt === 0}
                    onClick={() => openBulk(key, a)}
                  >
                    {BULK_ACTIONS[a].label}
                    {cnt > 0 && <span style={{ marginLeft: 6, opacity: 0.7 }}>({cnt})</span>}
                  </button>
                );
              })}
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Юзернейм</th>
                  <th>Название книги</th>
                  <th>Автор</th>
                  <th>Страна</th>
                  <th>Адрес</th>
                  <th>Tg/wp</th>
                  <th>Логист</th>
                  <th>Способ</th>
                  <th>Комментарий</th>
                  <th>Статус</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const action = NEXT_ACTION[r.status];
                  return (
                    <tr
                      key={r.userId}
                      className={r.username ? "tbl__row--clickable" : undefined}
                      style={{ opacity: rowLoadingId === r.userId ? 0.5 : undefined }}
                      onClick={() => handleRowClick(r)}
                    >
                      <td style={r.isInfluencer ? { color: "#FFC000" } : undefined}>
                        {r.username || `userId ${r.userId}`}
                      </td>
                      <td>{r.bookName || "—"}</td>
                      <td>{r.author || "—"}</td>
                      <td>{cityCountry(r)}</td>
                      <td>{r.address || "—"}</td>
                      <td>
                        {r.whatsapp && (
                          <a
                            href={waLink(r.whatsapp)}
                            target="_blank"
                            rel="noreferrer"
                            className="log-contact"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.whatsapp}
                          </a>
                        )}
                        {r.telegram && (
                          <div className="log-contact-tg">{r.telegram}</div>
                        )}
                        {!r.whatsapp && !r.telegram && "—"}
                      </td>
                      <td>{r.logist || "—"}</td>
                      <td>
                        {r.logMethod || "—"}
                        {/* Номер накладной — второй строкой под способом:
                            видно, у каких СДЭК-отправлений его ещё нет. */}
                        {r.logMethod === "sdek" && !!r.cdekNumber && (
                          <div className="log-track">{r.cdekNumber}</div>
                        )}
                      </td>
                      <td>{r.comment || "—"}</td>
                      <td>{r.status}</td>
                      <td>
                        {action ? (
                          <button
                            className="btn"
                            disabled={busyUserId === r.userId}
                            onClick={(e) => {
                              e.stopPropagation();
                              advance(r);
                            }}
                          >
                            {busyUserId === r.userId ? "…" : action.label}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
            )}
          </section>
          );
        })
      )}

      {selectedUser && (
        <User
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={() => {
            // Перезатягиваем логистику, чтобы изменения из попапа (адрес,
            // статус, логист и т.д.) подтянулись в таблицу.
            client
              .get<LogiRow[]>("/admin/archives/logistics")
              .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
              .catch(() => {});
          }}
          deadline="—"
        />
      )}

      {bulkPopup && (() => {
        const cfg = BULK_ACTIONS[bulkPopup.action];
        const day = days.find((d) => d.key === bulkPopup.dayKey);
        const candidates = (day?.items ?? []).filter(
          (r) => r.status === cfg.from && r.username
        );
        const anyChecked = candidates.some((r) => bulkChecked[r.userId]);
        // На шаге доставки — все отмеченные заказы, а не только проблемные:
        // экран заодно служит сверкой перед отправкой.
        const pending = candidates.filter((r) => bulkChecked[r.userId]);
        const allFilled = pending.every(
          (r) => !needsDelivery(r, bulkMethod[r.userId], bulkCdek[r.userId])
        );
        const methodOf = (r: LogiRow) => (bulkMethod[r.userId] ?? r.logMethod ?? "").trim();
        // «Задать всем» не трогает тех, у кого способ уже проставлен.
        const applyToAll = (m: string) =>
          setBulkMethod((prev) => {
            const next = { ...prev };
            pending.forEach((r) => {
              if (!r.logMethod) next[r.userId] = m;
            });
            return next;
          });
        const isDeliveryStep = bulkStep === "delivery";
        const dk = bulkPopup.dayKey;
        const dateLabel =
          dk === todayKey ? "Сегодня" : dk === yesterdayKey ? "Вчера" : dayKeyToLabel(dk);
        return (
          <div className="log-bulk-overlay" onClick={() => setBulkPopup(null)}>
            <div className="log-bulk-modal" onClick={(e) => e.stopPropagation()}>
              <div className="log-bulk-head">
                <h3 className="log-bulk-title">
                  {isDeliveryStep
                    ? "Способ доставки · " + pending.length
                    : cfg.label + " · " + dateLabel + ", " + weekdayAbbr(dk)}
                </h3>
                <button
                  className="log-bulk-close"
                  onClick={() => setBulkPopup(null)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </div>
              {isDeliveryStep ? (
                <>
                  
                  <div className="log-deliv-all">
                    <span className="log-deliv-all-label">Задать способ доставки всем</span>
                    <select
                      className="log-deliv-select"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) applyToAll(e.target.value);
                      }}
                    >
                      <option value="">—</option>
                      {LOG_METHOD_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="log-bulk-list">
                    {pending.map((r) => {
                      const m = methodOf(r);
                      const needNum = m === "sdek" && !(r.cdekNumber ?? "").trim();
                      return (
                        <div key={r.userId} className="log-deliv-row">
                          <span
                            className="log-deliv-name"
                            style={r.isInfluencer ? { color: "#FFC000" } : undefined}
                          >
                            {r.username}
                          </span>
                          {/* Способ правится и здесь: попап теперь открывается
                              всегда и служит сверкой перед отправкой, а на
                              сверке нужно иметь возможность исправить. */}
                          <select
                            className="log-deliv-select"
                            value={m}
                            onChange={(e) =>
                              setBulkMethod((p) => ({ ...p, [r.userId]: e.target.value }))
                            }
                          >
                            <option value="">—</option>
                            {LOG_METHOD_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                          {!(r.cdekNumber ?? "").trim() && (m === "" || m === "sdek") && (
                            <button
                              type="button"
                              className="log-deliv-back"
                              onClick={() => setWaybillRow({ row: r, inBulk: true })}
                            >
                              Накладная
                            </button>
                          )}
                          {needNum && (
                            <input
                              className="log-deliv-input"
                              type="text"
                              inputMode="numeric"
                              placeholder="Номер СДЭК"
                              value={bulkCdek[r.userId] ?? ""}
                              onChange={(e) =>
                                setBulkCdek((p) => ({ ...p, [r.userId]: e.target.value }))
                              }
                            />
                          )}
                          {/* Номер уже введён — показываем для сверки, но не
                              даём переписать случайным кликом. */}
                          {m === "sdek" && !!(r.cdekNumber ?? "").trim() && (
                            <span className="log-deliv-num">{r.cdekNumber}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="log-deliv-actions">
                    <button
                      className="log-deliv-back"
                      onClick={() => setBulkStep("list")}
                      disabled={bulkSaving}
                    >
                      Назад
                    </button>
                    <button
                      className="log-bulk-submit"
                      disabled={bulkSaving || !allFilled}
                      onClick={handleBulkSubmit}
                    >
                      {bulkSaving ? "Сохранение…" : cfg.label}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="log-bulk-list">
                    {candidates.length === 0 ? (
                      <div className="log-bulk-empty">Нет подходящих заказов</div>
                    ) : (
                      candidates.map((r) => (
                        <label key={r.userId} className="log-bulk-row">
                          <input
                            type="checkbox"
                            checked={!!bulkChecked[r.userId]}
                            onChange={(e) =>
                              setBulkChecked((p) => ({ ...p, [r.userId]: e.target.checked }))
                            }
                          />
                          <span
                            className="log-bulk-name"
                            style={r.isInfluencer ? { color: "#FFC000" } : undefined}
                          >
                            {r.username}
                          </span>
                          <span className="log-bulk-status">{r.status}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <button
                    className="log-bulk-submit"
                    disabled={bulkSaving || !anyChecked}
                    onClick={handleBulkSubmit}
                  >
                    {bulkSaving ? "Сохранение…" : cfg.label}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {waybillRow && (
        <CdekWaybillModal
          userId={waybillRow.row.userId}
          mode="create"
          onClose={() => setWaybillRow(null)}
          onSkip={() => {
            const { row, inBulk } = waybillRow;
            setWaybillRow(null);
            if (!inBulk) advance(row, true);
          }}
          onCreated={(cdekNumber) => {
            const { row, inBulk } = waybillRow;
            setWaybillRow(null);
            // Номер и способ «sdek» бэкенд уже записал — осталось сменить статус.
            const updated = { ...row, logMethod: "sdek", cdekNumber: cdekNumber || row.cdekNumber };
            setRows((prev) => prev.map((r) => (r.userId === row.userId ? updated : r)));
            if (inBulk) {
              setBulkMethod((p) => ({ ...p, [row.userId]: "sdek" }));
              if (!cdekNumber)
                setErrorPopup("Накладная создана, но СДЭК ещё не выдал номер. Откройте заказ через минуту.");
            } else if (cdekNumber) advance(updated, true);
            else
              setErrorPopup(
                "Накладная создана, но СДЭК ещё не выдал номер. Откройте заказ через минуту и отправьте снова."
              );
          }}
        />
      )}

      {errorPopup && (
        <div className="log-bulk-overlay" onClick={() => setErrorPopup(null)}>
          <div
            className="log-bulk-modal log-err-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="log-bulk-head">
              <h3 className="log-bulk-title">Не получилось</h3>
              <button
                className="log-bulk-close"
                onClick={() => setErrorPopup(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
            <div className="log-err-text">{errorPopup}</div>
            <button className="log-bulk-submit" onClick={() => setErrorPopup(null)}>
              Понятно
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Logistics;
